import React, { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import Heading from '@atlaskit/heading';
import { Stack, Inline, Text } from '@atlaskit/primitives';
import { AiIcon as AILogo } from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-icon/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import HelpPanelButton from '@atlassian/jira-help-panel-button';
import { useIntl } from '@atlassian/jira-intl';
import type { header_servicedeskSettingsVirtualAgentLanding$key } from '@atlassian/jira-relay/src/__generated__/header_servicedeskSettingsVirtualAgentLanding.graphql';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { Link } from '@atlassian/react-resource-router';
import messages from './messages';

export const Header: FC<{ featuresRef: header_servicedeskSettingsVirtualAgentLanding$key }> = ({
	featuresRef,
}) => {
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();

	const { isVirtualAgentAvailable } = useFragment(
		graphql`
			fragment header_servicedeskSettingsVirtualAgentLanding on VirtualAgentFeatures {
				isVirtualAgentAvailable
			}
		`,

		featuresRef,
	);

	const upgradeMessageIfSiteAdmin = fg('virtual_agent_rebrand_to_virtual_service_agent')
		? messages.upgradeToPremiumDescriptionV2
		: messages.upgradeToPremiumDescription;
	const upgradeMessageIfSiteNotAdmin = fg('virtual_agent_rebrand_to_virtual_service_agent')
		? messages.upgradeToPremiumNotSiteAdminDescriptionV2
		: messages.upgradeToPremiumNotSiteAdminDescription;

	return (
		<>
			<Inline space="space.100">
				<Heading size="large" as="h2">
					{fg('virtual_agent_rebrand_to_virtual_service_agent')
						? formatMessage(messages.headingV2)
						: formatMessage(messages.heading)}
				</Heading>
				{ff('check-for-ai-enabling-in-get-premium-banner_oxh6r') && (
					<AILogo
						size="large"
						label={
							fg('virtual_agent_rebrand_to_virtual_service_agent')
								? formatMessage(messages.aiLogoLabelV2)
								: formatMessage(messages.aiLogoLabel)
						}
					/>
				)}
			</Inline>
			<Stack space="space.100">
				<Text as="span">
					{formatMessage(
						fg('virtual_agent_rebrand_to_virtual_service_agent')
							? messages.descriptionSubheadingV2
							: messages.descriptionSubheading,
						{
							link: (...chunks: ReactNode[]) => (
								<HelpPanelButton
									articleId="3Il0EsHHL4CC43jddfd6dF"
									appearance="link"
									spacing="none"
									fallbackComponent={
										<Link
											target="_blank"
											rel="noopener noreferrer"
											href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-your-virtual-agent/"
										>
											{chunks}
										</Link>
									}
								>
									{chunks}
								</HelpPanelButton>
							),
						},
					)}
				</Text>
				<Text as="span">
					{formatMessage(
						fg('virtual_agent_rebrand_to_virtual_service_agent')
							? messages.machineLearningSubheadingV2
							: messages.machineLearningSubheading,
						{
							link: (...chunks: ReactNode[]) => (
								<HelpPanelButton
									articleId="5xSp1xirO88hf0LfQKLX1H"
									appearance="link"
									spacing="none"
									fallbackComponent={
										<Link
											target="_blank"
											rel="noopener noreferrer"
											href="https://support.atlassian.com/jira-service-management-cloud/docs/how-the-virtual-agents-data-set-is-trained/"
										>
											{chunks}
										</Link>
									}
								>
									{chunks}
								</HelpPanelButton>
							),
						},
					)}
				</Text>
				{!isVirtualAgentAvailable && (
					<span>
						{isSiteAdmin
							? formatMessage(upgradeMessageIfSiteAdmin)
							: formatMessage(upgradeMessageIfSiteNotAdmin)}
					</span>
				)}
			</Stack>
		</>
	);
};
